import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { SharedConstants, User } from '@formbird/types';
import {
  SharedUrlRoutes as SharedUrls,
  UtilArray,
  UtilDocumentId as IdGenerator,
  VersionManager
} from '@formbird/shared';
import { ClientConstants } from '../../constants/ClientConstants';
import { IndexedDBConstants } from '../../constants/IndexedDBConstants';
import { ClientAccessService } from '../access/client-access.service';
import { OfflineStatusService } from '../offline-status/offline-status.service';
import { LoggedInUserService } from '../user/logged-in-user.service';
import { DexieSearchService } from './dexie-search.service';
import { ElasticIndexedDBService } from './elastic-indexed-db.service';
import { IndexedDBConnectorService } from './indexed-dbconnector.service';
import { BroadcastService } from '../broadcast/broadcast.service';
import { Observable, Subscription } from 'rxjs';

import {cloneDeep} from 'lodash';
import { select } from '../../redux/decorators/select';

const logger = console;

@Injectable({
  providedIn: 'root'
})
export class IndexedDBService implements OnDestroy {
  user: User;
  userSub: Subscription;

  constructor(
    private http: HttpClient,
    private indexedDBConnectorService: IndexedDBConnectorService,
    private offlineStatusService: OfflineStatusService,
    private loggedInUserService: LoggedInUserService,
    private elasticIndexedDBService: ElasticIndexedDBService,
    private dexieSearchService: DexieSearchService,
    private clientAccessService: ClientAccessService,
    private broadcastService: BroadcastService
  ) {
    this.onDocumentUpdated();
    this.userSub = this.loggedInUserService.observableUser().subscribe((user: User) => this.user = user);
    // this.user$.subscribe((user: User) => this.user = user);
  }

  public constants = IndexedDBConstants;

  ngOnDestroy() {
    this.userSub.unsubscribe();
    this.terminateService();
  }

  public initialise() {

    logger.warn('-----TODO get unprocessed docs on initialise - but not on first initialisation');

    return this.getDatabase();
  }

  public terminateService() {
    return this.indexedDBConnectorService.closeDatabase();
  }

  public search(options) {

    const _self = this;

    if (options.searchOptions && options.searchOptions.dexieSearchFunction) {
      return _self.dexieSearchService.search(options.searchOptions);

    } else {
      return _self.elasticIndexedDBService.search(options);
    }
  }

  public insert(bean) {

    if (!bean.systemHeader) {
      bean.systemHeader = {};
    }

    // set versionId
    bean.systemHeader.versionId = IdGenerator.generateId();

    // set currentVersion for new document
    bean.systemHeader.currentVersion = true;

    // set created date for version
    bean.systemHeader.createdDate = new Date();

    const userId = this.user ? this.user.account.documentId : null;
    if (userId) {
      bean.systemHeader.createdBy = userId;
    }

    return this.persist(bean, ClientConstants.OPERATION_TYPE_POST, null);
  }

  public destroy(id, url, cachingRequired, templateId, previousVersionId) {

    const _self = this;

    return new Promise((resolve, reject) => {

      _self.getDocumentCurrentVersion(id, function(originalRecord) {

        const originalBean = originalRecord;

        // save version as an insert
        const versionDoc = VersionManager.createVersion(originalBean);

        _self.cacheDocument(versionDoc, true).then(
          function successFunc(record) {

            const newBean = cloneDeep(originalBean);

            newBean.systemHeader.deleted = true;

            // perform newBean update
            // Aside from the differences in data between originalBean and newbean, newBean will always
            // have its previousVersionId = originalBean's versionId. newBean will also be set as the
            // current version with a new versionId
            if (newBean.systemHeader.versionId) {
              newBean.systemHeader.previousVersionId = originalBean.systemHeader.versionId;
            }

            // update createdBy every time because when we update the doc it's a new version
            newBean.systemHeader.createdBy = _self.user ?
              _self.user.account.documentId : null;

            newBean.systemHeader.createdDate = new Date();

            newBean.systemHeader.versionId = IdGenerator.generateId();

            _self.persist(newBean, ClientConstants.OPERATION_TYPE_DELETE).then(
              function sucF(rec) {

                resolve(rec);

              },

              function errF(error) {

                reject(error);

              }
            );
          },

          function errorFunc(err) {

            reject(err);

          }
        );
      });
    });

  }

  public getDocument(url, id) {

    const _self = this;

    return new Promise(function(resolve, reject) {

      _self.getDocumentCurrentVersion(id, function(cacheObject) {

        if (cacheObject == null) {

          reject('Document not available in offline database.');

        } else {

          resolve(cacheObject);

        }

      });
    });
  }

  public getDatabase(): Promise<any> {
    return this.indexedDBConnectorService.getDatabase();
  }

  public closeDatabase() {
    this.getDatabase().then(function(db: any) {
      db.close();
    });
  }

  public backupData(): Promise<any> {

    const _self = this;

    return new Promise((resolve, reject) => {

      _self.getDatabase().then(function(db) {

        const promises = [];

        for (let i = 0; i < db.objectStoreNames.length; i++) {

          promises.push(
            new Promise((res, rej) => {
              const objectstore = db.objectStoreNames[i];

              const transaction = db.transaction([objectstore], 'readwrite');
              const content = [];

              transaction.oncomplete = function(event) {
                res({ name: objectstore, data: content });
              };

              transaction.onerror = function(event) {
                rej(event);
              };

              const handleResult = function(event) {

                const cursor = event.target.result;

                if (cursor) {
                  content.push({ key: cursor.key, value: cursor.value });
                  cursor.continue();
                }
              };

              const objectStore = transaction.objectStore(objectstore);
              objectStore.openCursor().onsuccess = handleResult;

            })
          );
        }

        Promise.all(promises).then(function successFunc(result) {

          let savedData = {};

          for (let i = 0; i < result.length; i++) {
            const tmp = result[i];
            savedData[tmp.name] = tmp.data;
          }

          savedData = JSON.stringify(savedData);

          resolve(savedData);

        });

      });
    });
  }

  public clearData(): Promise<any> {

    const _self = this;

    return new Promise((resolve, reject) => {

      _self.getDatabase().then(function(db) {

        const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;

        db.transaction('rw', db[tblName], (result) => {
          db[tblName].clear().then(() => {

            _self.loggedInUserService.setUserConfigItem('cachingEnabled', false);
            _self.loggedInUserService.setUserConfigItem('lastSyncDate', 0);
            _self.loggedInUserService.setUserConfigItem('idbCurrentVersion', 0);

            resolve(true);

          }, (e) => {

            logger.error('An error occured while clearing cached documents.');
            logger.error(e.stack || e);

            resolve(false);

          });
        }).catch(e => {

          logger.error('An error occured while clearing cached documents.');
          logger.error(e.stack || e);

          resolve(false);

        });
      });

    });
  }

  /**
   * set indexeddb indexes in document stored in IndexedDB so that it can be queried
   * @param document the document
   */
  private setIndexedDBIndexes(document) {

    if (!document || !document.systemHeader) {
      return;
    }

    const templateType = document.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_TEMPLATE;
    const withSearchIndexes = document[IndexedDBConstants.DB_INDEXES] && document[IndexedDBConstants.DB_INDEXES].length;

    if (templateType && withSearchIndexes) {

      const templateIndexes = document[IndexedDBConstants.DB_INDEXES];

      let indexes = this.loggedInUserService.getUserConfigItem('searchIndexes');

      indexes = UtilArray.union(indexes, templateIndexes, null);

      this.loggedInUserService.setUserConfigItem('searchIndexes', indexes);
    }
  }

  /**
   * cache a document
   * @param document the document
   * @returns PromiseLike<any> the Promise
   */
  public doCacheDocument(document, updateOfflineStatus): Promise<any> {

    const _self = this;

    return new Promise<any>((resolve, reject) => {

      if (!document.documentId) {

        _self.offlineStatusService.ignoreCacheDocument(document);

        logger.error('Invalid cached document: ' + JSON.stringify(document));

        resolve();

        return;
      }

      _self.setIndexedDBIndexes(document);

      _self.cacheDocument(document, false).then(
        function successFunc(cacheObject) {

          const doc = cacheObject;

          // update the last sync time in local storage with the time of this record if
          // it is later than the stored value
          if (updateOfflineStatus !== false) {
            _self.offlineStatusService.initialCacheQuerySuccess(doc);
          }

          resolve(doc);

        },
        function errorFunc(err) {

          logger.error('Error caching document: ' + document.documentId + ': ' + err);

          if (updateOfflineStatus !== false) {
            _self.offlineStatusService.ignoreCacheDocument(document);
          }
          resolve();
        }
      );
    });

  }

  /**
   * Updates or inserts a given row in the indexedb database under the table. For tables that uses inline keys
   * only (the primary key is on the object).
   * @param tblName - table name
   * @param record - record
   */
  public saveEntry(tblName, record, callback?) {

    if (!record) {

      if (typeof callback !== 'undefined') {
        callback(new Error('Document not specified in IndexedDB saveEntry'));
      }

      return;
    }

    if (record && record.systemHeader && record.systemHeader.versionId) {
//                logger.info('Saving IndexedDB entry with documentId: ' +
//                    record.documentId + '. systemHeader.versionId: ' + record.systemHeader.versionId );
    }

    this.getDatabase().then(function success(db) {

      db.transaction('rw', db[tblName], (result) => {

        db[tblName].put(record).then((data) => {

          if (callback != null) {
            callback();
          }

        }, (e) => {

          logger.error('Error on IndexedDBService.saveEntry:');
          logger.error(e);

          if (callback != null) {
            callback(e);
          }
        });
      }).catch(e => {

        logger.error('Error adding entry to IndexedDB: ' + e.stack || e);
        callback(e);

      });
    });
  }

  /**
   * Request errors will set the callback function's first argument to null.
   * Returns the file entry object as the callback function's first argument.
   * @param fileNo - the file number
   * @param callback - the callback
   */
  public getFile(fileNo, callback) {

    const tblName = IndexedDBConstants.OFFLINE_FILE_TABLE_NAME;
    const indexName = IndexedDBConstants.OFFLINE_FILE_INDEX_FILENO;
    const indexValue = fileNo;

    this.getEntries(tblName, indexName, indexValue, function(entriesArr) {

      if (entriesArr.length > 0) {
        callback(entriesArr[0]); // get the first entry -- which is the only entry, fileNos are unique
        return;
      }

      callback(null);
    });

  }

  /**
   * Record format should be:
   *    var record = {
                    fileNo : fileNo,
                    status : status, //'UPLOADED' or 'PENDING'
                    blob: blob,
                    uploadConfig : uploadConfig
                };
   */
  public saveFile(record, status?, errMessage?, errorCount?) {

    record.errorMessage = errMessage;
    record.errorCount = errorCount;

    const tblName = IndexedDBConstants.OFFLINE_FILE_TABLE_NAME;

    this.saveEntry(tblName, record, function(err) {

      if (err) {
        logger.error('Failed to save file to offline database: ' + err.message);
      }

    });

  }

  // ***End of Low level functions

  /**
   * Retrieve entries from a table
   * @param tblName - the table name
   * @param indexName - the index to search for
   * @param indexValue - the value to search for in the index
   * @param callback - callback that passes the entries in an array
   */
  public getEntries(tblName, indexName, indexValue, callback) {

    this.getDatabase().then(function success(db) {

      db[tblName].where(indexName).equals(indexValue).toArray(results => {

        callback(results);

      }).catch(function(error) {

        logger.error('Error getting dexie entries: ' + error.message);

      });
    });
  }

  /**
   * Delete entries from a table
   * @param tblName - the table name
   * @param indexName - the index to search for
   * @param indexValue - the value to search for in the index
   * @param callback - callback that passes deleted entries in an array. (optional)
   */
  public deleteEntries(tblName, indexName, indexValue, callback) {

    this.getDatabase().then(function success(db) {

      db[tblName].where(indexName).equals(indexValue).delete().then(deleteCount => {

        callback(null, deleteCount);

      }).catch(function(error) {

        callback(error, null);

      });
    });
  }

  /**
   * Seeks out the current version of the document in indexedDB document table and passes it via callback
   * @param id - either versionId or documentId. Evaluate it as documentId first, if that fails then treat it as a version id
   * @param callback - first param is the actual document. null if not found
   */
  public getDocumentCurrentVersion(id, callback) {

    const _self = this;

    if (id === undefined || id === null) {
      callback(null);
      return;
    }

    const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
    const indexName = IndexedDBConstants.DOCUMENT_INDEX_DOCUMENT_ID;
    const indexValue = id;

    _self.getEntries(tblName, indexName, indexValue, function(cachedObjects) {

      for (let x = 0; x < cachedObjects.length; x++) {

        const cacheObject = cachedObjects[x];

        if (cacheObject.systemHeader.currentVersion === true) {

          callback(cacheObject);
          return;

        }
      }

      _self.loadDocByVersion(id, callback);

    });

  }

  private loadDocsById(docId, callback) {

    if (docId === undefined || docId === null) {
      callback(null);
      return;
    }

    const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
    const indexName = IndexedDBConstants.DOCUMENT_INDEX_DOCUMENT_ID;
    const indexValue = docId;

    this.getEntries(tblName, indexName, indexValue, function(cachedObjects) {

      callback(cachedObjects);

    });
  }

  /*
   offlineDetails

   Once offline, if a document has this object under it, it means that it is subject to an operation yet
   to be submitted to the server.

   This object will have the following attributes {type, status, lastRequestStatus, tries}

   type - From ClientConstants: OPERATION_TYPE_DELETE, OPERATION_TYPE_PUT, OPERATION_TYPE_POST
   status - IndexedDBConstants: (1)OFFLINE_STATUS_UPLOADED, (2)OFFLINE_STATUS_PENDING,
   (3)OFFLINE_STATUS_UPLOADING (4)OFFLINE_STATUS_FAILED

   lastRequestStatus - http status of last attempt to upload this operation
   tries - integer count of the number of times this document's pending operation has been uploaded to the server

   Should not be used to cache documents. Use cacheDocument function instead.
   Used for saving documents that needs to be sent to and processed by the server.

   */
  private persist(cacheObject, operation?, options?) {

    const self = this;

    cacheObject.systemHeader.offlineDetails = {
      type: operation,
      status: IndexedDBConstants.OFFLINE_STATUS_PENDING,
      lastRequestStatus: '',
      tries: 0,
      deleteOnComplete: false, // if true, the cache object is removed from offline db after completing the operation
      updates: options ? options.updates : null,
      isUpdateFromPreviousVersion: options ? options.isUpdateFromPreviousVersion : false,
      createdDate: new Date(),
      makeCurrentVersion: options ? options.makeCurrentVersion : false,
      importPreviousVersion: options ? options.importPreviousVersion : false
    };

    return new Promise((resolve, reject) => {

      self.saveCacheObject(cacheObject, function onCacheObjectSaved(err, cacheObj) {

        if (err) {

          reject(err);

        } else {

          self.offlineStatusService.incPendingDocumentCount();
          resolve(cacheObj);

        }

      });

    });

  }

  public getUnprocessedDocs(callback) {

    const tbl = IndexedDBConstants.DOCUMENT_TABLE_NAME;
    const index = IndexedDBConstants.DOCUMENT_INDEX_PENDING_OPERATION;
    const searchValue = IndexedDBConstants.OFFLINE_STATUS_PENDING;

    this.getEntries(tbl, index, searchValue, callback); // function callback(docsArray)
  }

  public getProcessedCurrentVersionDocs(callback) {

    const tbl = IndexedDBConstants.DOCUMENT_TABLE_NAME;
    const index = IndexedDBConstants.DOCUMENT_INDEX_PENDING_OPERATION;
    const searchValue = IndexedDBConstants.OFFLINE_STATUS_UPLOADED;

    this.getEntries(tbl, index, searchValue, callback); // function callback(docsArray)
  }

  public getUnsentOfflineFilesInQueue(callback) {

    const tbl = IndexedDBConstants.OFFLINE_FILE_TABLE_NAME;
    const index = IndexedDBConstants.OFFLINE_FILE_INDEX_STATUS;
    const searchValue = IndexedDBConstants.OFFLINE_STATUS_PENDING;

    this.getEntries(tbl, index, searchValue, callback);

  }

  /**
   * Cache the document in IndexedDB for offline use
   * @param document - the document to cache
   */
  public cacheDocument(document, force) {

    const _self = this;

    return new Promise(function(resolve, reject) {

      _self.loadDocByVersion(document.systemHeader.versionId, docWithVersionFound);

      function docWithVersionFound(err, cacheObject) {

        // Do not cache the document twice unless forced to (when saving a version document. see jsonPatchUpdate)
        if (!force && cacheObject != null) {
          resolve(document);
          return;
        }

        _self.saveCacheObject(document, onCacheObjectSaved);

        function onCacheObjectSaved(error, doc) {

          if (error) {
            reject(error);
          } else {
            resolve(doc);
          }

        }
      }
    });
  }

  /**
   * cache document received from websocket
   * @param docInfo - the info used to load full document and changed versions
   * @param versions - the versions of document
   * @param updateOfflineStatus - flag indicates that should update offline caching status
   * @returns Promise
   */
  public cachePushedDocument(docInfo, versions, updateOfflineStatus?) {

    const _self = this;

    let currentVersion = null;
    versions.forEach(function(version) {

      if (version && version.systemHeader && version.systemHeader.currentVersion) {
        currentVersion = version;
      }

    });

    function cacheVersion(version) {

      return new Promise((resolve, reject) => {

        _self.saveCacheObject(version, function(err, savedVersion) {

          if (err) {
            reject(err);
          } else {
            resolve(savedVersion);
          }
        });
      });
    }

    function cacheExistingCurrentDoc() {

      // If the currentVersion in indexedDB is before the first prev version in above list so it would not
      // be set to non-currentVersion. This step set the value to false.
      _self.getDocumentCurrentVersion(docInfo.documentId, function(doc) {

        if (doc && currentVersion && doc.systemHeader.versionId !== currentVersion.systemHeader.versionId) {

          doc.systemHeader.currentVersion = false;

          _self.saveCacheObject(doc, cacheDone);
        }
      });
    }

    function cacheDone(err, version) {
      if (err) {
        logger.error('Caching version error: ' + err);
      } else {
        logger.info('Caching version done: ' + version.systemHeader.versionId);
      }
    }

    return new Promise((resolve, reject) => {

      const hasOfflineAccess = _self.clientAccessService.checkAccess(currentVersion, SharedConstants.OPERATION_TYPE_OFFLINE);
      if (hasOfflineAccess) {

        _self.setIndexedDBIndexes(currentVersion);

        if (docInfo.versionIds && docInfo.versionIds.length) { // in case of updating versions from pushed document

          const promises = [];
          versions.forEach(function(version) {
            promises.push(cacheVersion(version));
          });

          Promise.all(promises).then(
            function ok(versions2) {
              cacheExistingCurrentDoc();

              if (updateOfflineStatus !== false) {
                _self.offlineStatusService.initialCacheQuerySuccess(currentVersion);
              }

              resolve(versions2);
            },
            function fail(error) {
              const msg = 'Error in caching version of document: ' + docInfo.documentId;
              logger.error(msg);
              reject(new Error(msg));
            }
          );

        } else {    // in case of updating each current version from websocket reconnect event or browser reloading

          cacheVersion(currentVersion).then(cacheExistingCurrentDoc).then(function() {

            if (updateOfflineStatus !== false) {
              _self.offlineStatusService.initialCacheQuerySuccess(currentVersion);
            }

            resolve(currentVersion);

          });
        }
      }
    });
  }

  /**
   * load a document by systemHeader.versionId
   * @param versionId - the version id
   * @param callback - will be called at completion of retrieving data. Parameters are:
   *                   err - an error object
   *                   result - the document found matching systemHeader.versionId
   */
  private loadDocByVersion(versionId, callback?) {

    const _self = this;

    // if no callback then promisify
    if (!callback) {
      return new Promise((resolve, reject) => {
        _self.loadDocByVersion(versionId, (err, result) => {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        });
      });
    }

    if (!versionId) {

      callback(new Error('versionId not specified in loadDocByVersion'));
      return;
    }

    this.getDatabase().then(function success(db) {

      db[IndexedDBConstants.DOCUMENT_TABLE_NAME].where(IndexedDBConstants.DOCUMENT_INDEX_VERSION_ID).equals(versionId)
        .first(result => {

        // return null as error object because there is no error
        callback(null, result);

      }).catch(function(error) {

        logger.error('Error getting document version ' + versionId + '. Error: ' + error.message);
        callback(error);

      });
    });
  }

  public loadCachedDocByVersion(documentId, versionId, callback) {

    const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
    const indexName = IndexedDBConstants.DOCUMENT_INDEX_DOCUMENT_ID;
    const indexValue = documentId;

    this.getEntries(tblName, indexName, indexValue, function(entriesArr) {

      if (entriesArr.length > 0) {

        for (let i = 0; i < entriesArr.length; i++) {

          if (entriesArr[i].systemHeader.versionId === versionId) {

            callback(entriesArr[i]);
            return;

          }
        }
      }

      logger.info('No cached document found for this version: ' + versionId + ' of doc: ' + documentId);
      callback(null);

    });
  }

  private cacheAttachedFilesOffline(document) {
    // Cache file attachments sequence here
    // Searches for a file attachment in the document
    // Just visit every array property and if it contains an item with a property
    // named fileNo then load that file from the server into the cache system

    const properties = Object.keys(document);

    for (let i = 0; i < properties.length; i++) {

      const pValue = document[properties[i]];

      if (pValue instanceof Array) {

        for (let ix = 0; ix < pValue.length; ix++) {

          const specimen = pValue[ix];

          if (specimen && specimen.fileNo) { // this is a file attachment by ftUploader

            this.loadFileAsync(specimen.fileNo);

          }
        }
      }
    }
  }

  public toBase64Str(blob) {
    return new Promise((resolve, reject) => {

      const reader = new FileReader();

      reader.onloadend = function() {
        resolve(reader.result);
      };

      reader.onerror = function() {
        reject('Error converting to base64str blob.');
      };

      reader.readAsDataURL(blob);
    });
  }

  // caches file blob into indexeddb
  private loadFileAsync(fileNo) {

    const _self = this;

    return new Promise((resolve, reject) => {

      const url = SharedUrls.serverRoutes.loadFile + '/' + fileNo;

      _self.http.get(url, {responseType: 'blob'}).subscribe(
        response => {
          const data = response;

          if (data) {
            _self.toBase64Str(data).then(function(base64str) {

              const offlineFileRecord = {
                fileNo: fileNo,
                status: IndexedDBConstants.OFFLINE_STATUS_UPLOADED,
                uploadConfig: {
                  data: {
                    uploadedFile: base64str
                  }
                }
              };

              _self.saveFile(offlineFileRecord);
            });

          } else {
            logger.warn('No data from remote file.');
          }

        });
    });

  }

  /**
   *  Removes all versions of this document and leave the current version behind.
   *  'document' here is the current version fetched from the server. See DataService.getDocumentOfflineMode
   */
  public documentReset(documentId) {

    const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
    const indexName = IndexedDBConstants.DOCUMENT_INDEX_DOCUMENT_ID;
    const indexValue = documentId;

    this.deleteEntries(tblName, indexName, indexValue, function(err, count) {
      logger.info('Document reset cleared ' + count + ' versions of ' + indexValue);
    });

  }

  public saveCacheObject(offlineCacheObject, callback) {
    const tbl = IndexedDBConstants.DOCUMENT_TABLE_NAME;

    // versionId is the primary key, hence, redundancy is avoided in cache see IndexedDBConnectorService

    this.saveEntry(tbl, offlineCacheObject, function(err) {

      if (!err) {
        callback(null, offlineCacheObject);
      } else {
        callback(err);
      }
    });

    // if there are blobs, then save them into cache too
    this.cacheAttachedFilesOffline(offlineCacheObject);
  }

  private onDocumentUpdated() {

    const _self = this;

    this.broadcastService.on('document:updated').subscribe(data => {

      if (data) {

        const record = data[0];
        if (record && record.systemHeader && record.systemHeader.systemType === SharedConstants.SYSTEM_TYPE_TEMPLATE) {

          const indexes = _self.loggedInUserService.getUserConfigItem('searchIndexes');

          if (record.indexedDBIndexes && record.indexedDBIndexes.length) {

            let needtoUpgraded = false;

            record.indexedDBIndexes.forEach(function(indexName) {
              const isUpgraded = UtilArray.add(indexes, indexName, true);
              needtoUpgraded = isUpgraded ? isUpgraded : needtoUpgraded;
            });

            if (needtoUpgraded) {
              _self.loggedInUserService.setUserConfigItem('searchIndexes', indexes);

              _self.indexedDBConnectorService.upgradeOfflineDatabase();
            }
          }
        }
      }
    });
  }

  async jsonPatchUpdate(newBean, originalBean, options) {

    // Use the doc saved in indexeddb because original bean has been mutated
    // to not have offlineDetails. See https://mantis.formbird.com/view.php?id=14351
    const orig = await this.loadDocByVersion(originalBean.systemHeader.versionId);
    const versionDoc = VersionManager.createVersion(orig);

    await this.cacheDocument(versionDoc, true);

    // perform newBean update
    // Aside from the differences in data between originalBean and newbean, newBean will always have
    // its previousVersionId = originalBean's versionId. newBean will also be set as the current version
    // with a new versionId

    newBean.systemHeader.previousVersionId = versionDoc.systemHeader.versionId;
    // if versionId not yet generated
    if (newBean.systemHeader.versionId === versionDoc.systemHeader.versionId) {
      newBean.systemHeader.versionId = IdGenerator.generateId();
    }
    // update createdBy every time because when we update the doc it's a new version
    newBean.systemHeader.createdBy = this.user ?
      this.user.account.documentId : null;

    newBean.systemHeader.createdDate = new Date();

    const savedDocument = await this.persist(newBean, ClientConstants.OPERATION_TYPE_PUT, options);
    return savedDocument;

  }

  public deepDiffUpdate(newBean, originalBean, options) {
    return this.jsonPatchUpdate(newBean, originalBean, options);
  }

  public doCacheBulkDocuments(documents, updateOfflineStatus): Promise<any> {

    const _self = this;

    return new Promise<any>((resolve, reject) => {

      for (let i = 0; i < documents.length; i++) {
          try {
            let document;
            document = documents[i];
            _self.setIndexedDBIndexes(document);
            // if there are blobs, then save them into cache too
            this.cacheAttachedFilesOffline(document);
          } catch(err){
              logger.error(err);
          }
      }

      this.getDatabase().then(function success(db) {
        const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
        db.transaction('rw', db[tblName], (result) => {

          db[tblName].bulkPut(documents).then((data) => {

            if (updateOfflineStatus !== false) {
              _self.offlineStatusService.offlineStatus.currentCacheCount += (documents.length-1);
              _self.offlineStatusService.initialCacheQuerySuccess(documents[documents.length -1]);
            }
            resolve(data);

          }, (e) => {

            logger.error('Error caching bulk documents');
            logger.error(e);

            reject(e);
          });
        }).catch(e => {

          logger.error('Error adding entry to IndexedDB: ' + e.stack || e);
          reject(e);

        });

      });

    })
  }

}
